// src/components/proposal-formats/FormatDocumentSlotRow.tsx

import { useEffect, useState } from "react";
import type { FormatDocumentSlot } from "@/lib/opportunity/formatDocumentSlotTypes";
import { isPersistedDocumentSlotId } from "@/lib/opportunity/formatDocumentSlotTypes";
import type { UpsertFormatDocumentSlotPayload } from "@/lib/opportunity/formatDocumentSlotClient";

export type FormatDocumentSlotRowProps = {
  slot: FormatDocumentSlot;
  productType: string;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDelete: boolean;
  onSave: (payload: UpsertFormatDocumentSlotPayload) => Promise<{ ok: boolean; error?: string }>;
  onDelete?: (id: string) => Promise<{ ok: boolean; error?: string }>;
  onMove: (direction: "up" | "down") => void;
};

export function FormatDocumentSlotRow(props: FormatDocumentSlotRowProps) {
  const { slot, productType, canMoveUp, canMoveDown, canDelete, onSave, onDelete, onMove } = props;
  const [label, setLabel] = useState(slot.label);
  const [helperText, setHelperText] = useState(slot.helper_text ?? "");
  const [requiredBeforeSigned, setRequiredBeforeSigned] = useState(slot.required_before_signed);
  const [busy, setBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [deleteMsg, setDeleteMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setLabel(slot.label);
    setHelperText(slot.helper_text ?? "");
    setRequiredBeforeSigned(slot.required_before_signed);
    setSaveMsg(null);
    setDeleteMsg(null);
  }, [slot.id, slot.updated_at]);

  const dirty =
    label !== slot.label ||
    helperText !== (slot.helper_text ?? "") ||
    requiredBeforeSigned !== slot.required_before_signed;

  async function handleSave() {
    if (!label.trim()) return;
    setBusy(true);
    setSaveMsg(null);
    const r = await onSave({
      id: isPersistedDocumentSlotId(slot.id) ? slot.id : undefined,
      format_key: slot.format_key,
      product_type: slot.product_type.trim() || productType,
      slot_key: slot.slot_key,
      label: label.trim(),
      helper_text: helperText.trim() || null,
      sort_order: slot.sort_order,
      required_before_signed: requiredBeforeSigned,
    });
    setBusy(false);
    setSaveMsg({ ok: r.ok, text: r.ok ? "Saved." : (r.error || "Save failed.") });
  }

  async function handleDelete() {
    if (!onDelete || !window.confirm(`Remove slot "${slot.label}"?`)) return;
    setBusy(true);
    setDeleteMsg(null);
    const r = await onDelete(slot.id);
    setBusy(false);
    if (!r.ok) setDeleteMsg({ ok: false, text: r.error || "Delete failed." });
  }

  return (
    <details className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900" open>
      <summary className="cursor-pointer list-none px-3 py-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-zinc-500">
            {slot.slot_key}
          </span>
          <p className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">{label}</p>
        </div>
        {requiredBeforeSigned ? (
          <span className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-200">
            Required before Signed
          </span>
        ) : null}
      </summary>
      <div className="px-3 pb-3 space-y-2 border-t border-gray-100 dark:border-zinc-800 pt-2">
        <label className="block text-xs">
          <span className="text-gray-500">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
          />
        </label>
        <label className="block text-xs">
          <span className="text-gray-500">Helper text (optional)</span>
          <input
            type="text"
            value={helperText}
            onChange={(e) => setHelperText(e.target.value)}
            className="mt-1 w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-zinc-800 dark:border-zinc-700"
          />
        </label>
        <label className="flex items-start gap-2 text-xs text-gray-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={requiredBeforeSigned}
            onChange={(e) => setRequiredBeforeSigned(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="font-medium text-gray-800 dark:text-zinc-200">Required before Signed</span>
            <span className="block text-gray-500 dark:text-zinc-400 mt-0.5">
              When checked, sales cannot mark an opportunity Signed until at least one file is uploaded
              to this slot. Only applies to this product type.
            </span>
          </span>
        </label>
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="flex gap-1">
            <button
              type="button"
              disabled={!canMoveUp || busy}
              onClick={() => onMove("up")}
              className="px-2 py-0.5 rounded border border-gray-300 text-[10px] disabled:opacity-40"
            >
              ↑
            </button>
            <button
              type="button"
              disabled={!canMoveDown || busy}
              onClick={() => onMove("down")}
              className="px-2 py-0.5 rounded border border-gray-300 text-[10px] disabled:opacity-40"
            >
              ↓
            </button>
            {canDelete && onDelete ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleDelete()}
                  className="px-2 py-0.5 rounded border border-red-300 text-[10px] text-red-700"
                >
                  Remove
                </button>
                {deleteMsg ? (
                  <span className={deleteMsg.ok ? "text-emerald-600 text-xs" : "text-red-600 text-xs"}>
                    {deleteMsg.text}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {saveMsg ? (
              <span className={saveMsg.ok ? "text-emerald-600 text-xs" : "text-red-600 text-xs"}>
                {saveMsg.text}
              </span>
            ) : null}
            <button
              type="button"
              disabled={busy || !dirty}
              onClick={() => void handleSave()}
              className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-xs disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </details>
  );
}
